/**
 * Analytics Dashboard — login history, page visits, user summaries, AI token usage.
 * Admin-only view.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Box, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography, Chip, IconButton, Tooltip, TextField,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { MoftakiBotIcon } from '../../components/shared/MoftakiBotIcon';
import { alpha, useTheme } from '@mui/material/styles';
import apiClient from '../../services/apiClient';

interface LoginEntry { Email: string; DisplayName: string; Role: string; ActivityTimestamp: string }
interface PageStat { PageUrl: string; visitCount: number; uniqueUsers: number }
interface UserSummary {
  Email: string; DisplayName: string; Role: string; Status: string;
  LastLoginDate: string | null; loginCount: number; pageVisitCount: number; totalSeconds: number | null;
}
interface TokenUser {
  DisplayName: string; Email: string; Role: string;
  ConversationCount: number; TotalInputTokens: number; TotalOutputTokens: number; TotalMessages: number;
}
interface TokenTotals { TotalInput: number; TotalOutput: number; TotalConversations: number; TotalMessages: number }

// Claude API pricing (USD per million tokens) — update when pricing changes
const PRICING: Record<string, { input: number; output: number }> = {
  'claude-sonnet-4-6': { input: 3.0, output: 15.0 },
  'claude-haiku-4-5-20251001': { input: 0.8, output: 4.0 },
  'claude-opus-4-6': { input: 15.0, output: 75.0 },
};
const DEFAULT_MODEL = 'claude-sonnet-4-6';
const DEFAULT_USD_TO_CAD = 1.38;

const cellSx = { fontSize: '0.78rem', py: 0.5, px: 1 };
const headerSx = { ...cellSx, fontWeight: 700 };

const roleColor = (role: string) => {
  switch (role) {
    case 'admin': case 'superadmin': return 'error';
    case 'technician': return 'primary';
    default: return 'default';
  }
};

const fmtDuration = (seconds: number) => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
};

const fmtTokens = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : String(n);

function calcCost(inputTokens: number, outputTokens: number, model = DEFAULT_MODEL): number {
  const p = PRICING[model] || PRICING[DEFAULT_MODEL];
  return (inputTokens / 1_000_000) * p.input + (outputTokens / 1_000_000) * p.output;
}

export const AnalyticsDashboard = () => {
  const theme = useTheme();
  const [logins, setLogins] = useState<LoginEntry[]>([]);
  const [pages, setPages] = useState<PageStat[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [tokenUsers, setTokenUsers] = useState<TokenUser[]>([]);
  const [tokenTotals, setTokenTotals] = useState<TokenTotals | null>(null);
  const [usdToCad, setUsdToCad] = useState(DEFAULT_USD_TO_CAD);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, p, u, t] = await Promise.all([
        apiClient.get('/analytics/logins?limit=20'),
        apiClient.get('/analytics/pages?days=30'),
        apiClient.get('/analytics/users'),
        apiClient.get('/chat/usage-stats'),
      ]);
      setLogins(l.data);
      setPages(p.data);
      setUsers(u.data);
      setTokenUsers(t.data.users);
      setTokenTotals(t.data.totals);
    } catch { /* non-critical */ }
    setLoading(false);
  }, []);

  const refreshRate = async () => {
    try {
      const res = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await res.json();
      if (data.rates?.CAD) setUsdToCad(data.rates.CAD);
    } catch { /* keep default */ }
  };

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const totalCostUsd = tokenTotals ? calcCost(tokenTotals.TotalInput, tokenTotals.TotalOutput) : 0;
  const totalCostCad = totalCostUsd * usdToCad;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Analytics</Typography>

      {/* AI Token Usage — NEW */}
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1, backgroundColor: alpha('#7C4DFF', 0.06), display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoftakiBotIcon size={20} />
            <Typography variant="subtitle2" fontWeight={700}>Moftaki AI — Token Usage & Cost</Typography>
          </Box>
          {tokenTotals && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip size="small" label={`${fmtTokens(tokenTotals.TotalInput)} in / ${fmtTokens(tokenTotals.TotalOutput)} out`}
                sx={{ fontSize: '0.7rem', height: 22 }} />
              <Chip size="small" color="primary" label={`$${totalCostUsd.toFixed(2)} USD / $${totalCostCad.toFixed(2)} CAD`}
                sx={{ fontSize: '0.7rem', height: 22 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>1 USD =</Typography>
                <TextField size="small" value={usdToCad} type="number"
                  onChange={(e) => setUsdToCad(parseFloat(e.target.value) || DEFAULT_USD_TO_CAD)}
                  sx={{ width: 70, '& input': { fontSize: '0.72rem', py: 0.25, px: 0.5 } }}
                  inputProps={{ step: 0.01 }} />
                <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>CAD</Typography>
                <Tooltip title="Fetch live USD→CAD rate">
                  <IconButton size="small" onClick={refreshRate}><Refresh sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>User</TableCell>
                <TableCell sx={headerSx}>Role</TableCell>
                <TableCell sx={headerSx} align="right">Chats</TableCell>
                <TableCell sx={headerSx} align="right">Messages</TableCell>
                <TableCell sx={headerSx} align="right">Input Tokens</TableCell>
                <TableCell sx={headerSx} align="right">Output Tokens</TableCell>
                <TableCell sx={headerSx} align="right">Cost (USD)</TableCell>
                <TableCell sx={headerSx} align="right">Cost (CAD)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokenUsers.map((u) => {
                const costUsd = calcCost(u.TotalInputTokens, u.TotalOutputTokens);
                return (
                  <TableRow key={u.Email} hover>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{u.DisplayName}</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{u.Email}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Chip label={u.Role} size="small" color={roleColor(u.Role) as any} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell sx={cellSx} align="right">{u.ConversationCount}</TableCell>
                    <TableCell sx={cellSx} align="right">{u.TotalMessages}</TableCell>
                    <TableCell sx={cellSx} align="right">{fmtTokens(u.TotalInputTokens)}</TableCell>
                    <TableCell sx={cellSx} align="right">{fmtTokens(u.TotalOutputTokens)}</TableCell>
                    <TableCell sx={cellSx} align="right">${costUsd.toFixed(4)}</TableCell>
                    <TableCell sx={cellSx} align="right">${(costUsd * usdToCad).toFixed(4)}</TableCell>
                  </TableRow>
                );
              })}
              {tokenUsers.length === 0 && (
                <TableRow><TableCell colSpan={8} sx={cellSx} align="center">No chat usage yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* User Summaries */}
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1, backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
          <Typography variant="subtitle2" fontWeight={700}>User Activity Summary</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>User</TableCell>
                <TableCell sx={headerSx}>Role</TableCell>
                <TableCell sx={headerSx}>Status</TableCell>
                <TableCell sx={headerSx} align="right">Logins</TableCell>
                <TableCell sx={headerSx} align="right">Pages</TableCell>
                <TableCell sx={headerSx} align="right">Time</TableCell>
                <TableCell sx={headerSx}>Last Login</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.Email} hover>
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{u.DisplayName}</Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{u.Email}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Chip label={u.Role} size="small" color={roleColor(u.Role) as any} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell sx={cellSx}>{u.Status}</TableCell>
                  <TableCell sx={cellSx} align="right">{u.loginCount || '\u2014'}</TableCell>
                  <TableCell sx={cellSx} align="right">{u.pageVisitCount || '\u2014'}</TableCell>
                  <TableCell sx={cellSx} align="right">{u.totalSeconds > 0 ? fmtDuration(u.totalSeconds) : '\u2014'}</TableCell>
                  <TableCell sx={cellSx}>
                    {u.LastLoginDate ? new Date(u.LastLoginDate).toLocaleString() : '\u2014'}
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow><TableCell colSpan={7} sx={cellSx} align="center">No data yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Recent Logins */}
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1, backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
          <Typography variant="subtitle2" fontWeight={700}>Recent Logins</Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>User</TableCell>
                <TableCell sx={headerSx}>Role</TableCell>
                <TableCell sx={headerSx}>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logins.map((l, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={cellSx}>{l.DisplayName} ({l.Email})</TableCell>
                  <TableCell sx={cellSx}><Chip label={l.Role} size="small" color={roleColor(l.Role) as any} variant="outlined" sx={{ fontSize: '0.68rem' }} /></TableCell>
                  <TableCell sx={cellSx}>{new Date(l.ActivityTimestamp).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {logins.length === 0 && (
                <TableRow><TableCell colSpan={3} sx={cellSx} align="center">No login data yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Top Pages */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1, backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
          <Typography variant="subtitle2" fontWeight={700}>Most Visited Pages (30 days)</Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>Page</TableCell>
                <TableCell sx={headerSx} align="right">Visits</TableCell>
                <TableCell sx={headerSx} align="right">Unique Users</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pages.map((p, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={cellSx}>{p.PageUrl || '/'}</TableCell>
                  <TableCell sx={cellSx} align="right">{p.visitCount}</TableCell>
                  <TableCell sx={cellSx} align="right">{p.uniqueUsers}</TableCell>
                </TableRow>
              ))}
              {pages.length === 0 && (
                <TableRow><TableCell colSpan={3} sx={cellSx} align="center">No page visit data yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
